import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useHomeHealthQualificationsForVisitType } from "@src/appV2/redesign/HomeHealth/hooks/useHomeHealthQualificationsForVisitType";
import { Text } from "@src/appV2/redesign/components/Text";

import { PricingType, VISIT_TYPE_LABELS, VisitType } from "../types";
import {
  MobileAddressAutocompleteField,
  type SelectedAddress,
} from "./MobileAddressAutocompleteField";

export interface Step1Data {
  patientName: string;
  address: string;
  oasis: boolean;
  visitType: string;
  workerRequirement: string;
  pricingType: PricingType;
}

interface Props {
  data: Step1Data;
  onChange: (field: keyof Step1Data, value: string | boolean | PricingType) => void;
  onAddressSelect: (address: SelectedAddress) => void;
}

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

export function MobileCreateCaseStep1({ data, onChange, onAddressSelect }: Props) {
  // Worker requirements are constrained by visit type (e.g. an Admission visit
  // only allows RN/PT), matching the desktop case form. This prevents posting
  // impossible combinations like a Caregiver admission visit (GRO-2542).
  const allowedWorkerRequirements = useHomeHealthQualificationsForVisitType({
    visitType: data.visitType as VisitType,
  });

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Text semibold variant="body1">
          What&apos;s the patient name?
        </Text>
        <TextField
          fullWidth
          placeholder="Ilorum ipsum, etc..."
          value={data.patientName}
          onChange={(e) => {
            onChange("patientName", e.target.value);
          }}
          sx={inputSx}
          inputProps={{ "data-dd-privacy": "mask" }}
        />
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            checked={data.oasis}
            onChange={(e) => {
              onChange("oasis", e.target.checked);
            }}
          />
        }
        label="OASIS"
      />

      <Stack spacing={1}>
        <Text semibold variant="body1">
          What&apos;s the address?
        </Text>
        <MobileAddressAutocompleteField
          value={data.address}
          placeholder="Start typing an address..."
          onInputChange={(addressValue) => {
            onChange("address", addressValue);
          }}
          onSelect={onAddressSelect}
          sx={inputSx}
        />
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          What kind of visits?
        </Text>
        <FormControl fullWidth sx={inputSx}>
          <Select
            displayEmpty
            value={data.visitType}
            onChange={(e) => {
              onChange("visitType", e.target.value);
              // Clear the worker requirement: the previous selection may not be
              // valid for the new visit type (see allowedWorkerRequirements).
              onChange("workerRequirement", "");
            }}
            renderValue={(value) =>
              value ? VISIT_TYPE_LABELS[value as VisitType] : "Select an option"
            }
          >
            {Object.entries(VISIT_TYPE_LABELS).map(([type, label]) => (
              <MenuItem key={type} value={type}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          What kind of professional?
        </Text>
        <FormControl fullWidth sx={inputSx} disabled={!data.visitType}>
          <Select
            displayEmpty
            value={data.workerRequirement}
            onChange={(e) => {
              onChange("workerRequirement", e.target.value);
            }}
            renderValue={(value) =>
              value
                ? (value as string)
                : data.visitType
                  ? "Select an option"
                  : "Select a visit type first"
            }
          >
            {allowedWorkerRequirements.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={0.5}>
        <Text semibold variant="body1">
          Type
        </Text>
        <Text variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          Paid per visit or per hour
        </Text>
        <ToggleButtonGroup
          value={data.pricingType}
          exclusive
          onChange={(_, newValue: PricingType | null) => {
            if (newValue !== null) {
              onChange("pricingType", newValue);
            }
          }}
          sx={{ mt: 1 }}
        >
          <ToggleButton value={PricingType.PER_VISIT}>Per visit</ToggleButton>
          <ToggleButton value={PricingType.PER_HOUR}>Per hour</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
